// Patch notes catalog. Newest entries first.
//
// === WORKFLOW (read this before adding entries) =============================
// The modal auto-pops on a player's next login when LATEST_VERSION (the
// `version` field of PATCH_NOTES[0]) does not match the value stored in
// localStorage under `loria_last_patch_seen`. After they close the modal,
// their last-seen is bumped to LATEST_VERSION and they're not prompted
// again until you add a new entry with a NEW version string.
//
// IMPORTANT: these `version` strings are MILESTONE names and are
// intentionally DECOUPLED from the Heroku release counter (v734, v735, ...)
// and from package.json `loriaDisplayVersion`. The Heroku counter ticks
// every deploy (including hotfixes) — we do NOT want to spam players with
// a modal every push.
//
// When to add a new entry:
//   ✅ Shipped a batch of player-facing features / polish (every few
//      releases or weekly cadence)
//   ✅ Notable balance pass, content drop, or systemic change
//   ❌ Single bug-fix hotfix
//   ❌ Internal refactor with no visible change
//   ❌ Server-only / infrastructure work
//
// When in doubt: don't add an entry, edit the latest one in place (its
// version string stays the same so already-prompted players aren't
// re-prompted). Only bump the version string when you decide it's worth
// surfacing.
// ============================================================================

export const PATCH_NOTES = [
  {
    version: 'v685',
    date: '2026-05-18',
    title: 'UX Polish, Cinematic & Combat Clarity',
    highlights: [
      { icon: '🎬', text: 'Opening cinematic plays once after character creation — skippable with Esc/Space.' },
      { icon: '⚔️', text: 'Spell tooltips show full damage range with your MEN modifier applied.' },
      { icon: '🌟', text: 'Level-up celebration overhaul — clearer stat preview, card-pick flow, and post-battle summary.' },
      { icon: '🖼️', text: 'NPC portraits now resolve through entry-node fallbacks (Lucious, Telleran, others).' },
      { icon: '⚙️', text: 'Settings panel reachable from the quick-settings popover on all layouts (mobile, modern, classic).' },
      { icon: '📰', text: 'Patch notes are now correctly shown on mobile and modern desktop layouts.' },
      { icon: '📡', text: 'Reconnect banner and AFK auto-logout prompt now work on all device layouts.' },
    ],
  },
  {
    version: 'v641',
    date: '2026-05-06',
    title: 'Meta-Game, Session & Onboarding QoL',
    highlights: [
      { icon: '🔔', text: 'Notification Center bell with badge for unread mail, claimable bounties, expiring market listings, and guild invites.' },
      { icon: '📰', text: 'Patch Notes modal — you\'re reading it now! Auto-pops once per release.' },
      { icon: '🔢', text: 'Compact number toggle in Settings (12,345 → 12.3k) for gold, crowns, and XP.' },
      { icon: 'ℹ️', text: 'Help (?) badges throughout the UI explain market commission, CHA discount, rested XP, world tier, and Patron perks.' },
      { icon: '💤', text: 'AFK detection — keep-alive prompt after 5 min idle so you don\'t accidentally lose your session.' },
      { icon: '🎲', text: '"Surprise me" random-character button + class-loadout templates on character creation.' },
      { icon: '📍', text: 'Position resume — log out anywhere and resume right where you left off.' },
    ],
  },
  {
    version: 'v640',
    date: '2026-05-06',
    title: 'Combat & Feedback',
    highlights: [
      { icon: '🎯', text: 'Threat meters in fellowship combat show who has aggro on each enemy.' },
      { icon: '💀', text: 'Death recap shows the last 10 turns leading to your defeat.' },
      { icon: '📊', text: 'Stat preview on level-up — see your new HP/MP/ATT/DEF before committing.' },
      { icon: '↩️', text: 'Skill-tree 60-second soft-undo — accidental allocations refundable for free.' },
      { icon: '⌨️', text: 'Hotkey rebinding in Settings → Hotkeys.' },
      { icon: '🔮', text: 'Spell tooltips show damage range with your current MEN modifier baked in.' },
      { icon: '🚶', text: 'Click any visible passable tile to auto-walk there via BFS pathfinding.' },
      { icon: '📅', text: 'Daily Dashboard 📅 — at-a-glance free Delve, bounties, quests, and Patron status.' },
      { icon: '🏆', text: 'Achievement Gallery 🏆 — full catalog with progress bars and unlock dates.' },
    ],
  },
  {
    version: 'v639',
    date: '2026-05-06',
    title: 'Inventory & Economy QoL',
    highlights: [
      { icon: '🔒', text: 'Item locking — click ⭐ to protect items from accidental sell, salvage, or drop.' },
      { icon: '🛒', text: 'Bulk sell-junk by rarity in shops with a confirmation preview.' },
      { icon: '↩️', text: 'Vendor buyback — recover items you sold this session.' },
      { icon: '⚒️', text: 'Bulk salvage by rarity in the crafting panel.' },
      { icon: '📦', text: 'One-click "Deposit Mats" in stash + auto-stack consolidation.' },
      { icon: '🗄️', text: 'Stash tabs — 4 tabs unlocked at lvl 1/10/25/40 (or via Crown Shop).' },
      { icon: '⚔️', text: '"Equip Best" auto-pick button per slot in inventory.' },
      { icon: '🔍', text: 'Vendor item-comparison tooltips and inventory rarity filter.' },
    ],
  },
];

/**
 * Latest milestone version string, e.g. 'v685'.
 * Drives the auto-pop check in PatchNotesModal — see workflow notes at top of file.
 * NOT the same as the Heroku release counter or package.json `loriaDisplayVersion`.
 */
export const LATEST_VERSION = PATCH_NOTES[0]?.version || 'v0';
